'use strict'

import sjcl from 'sjcl'

import XhrService from './xhrservice.coffee'
import ApiService from './apiservice.coffee'
import LanguageService from './languageservice'
import LoginService from './loginservice.coffee'
import localStorageService from './localstorageservice'
import AnalyticsService from './analyticsservice.coffee'
import { $q, $log } from '../helpers/angular-migration-helpers'
import { debugLoggingId } from '../helpers/logging'
import UploadToken from '../models/uploadToken.coffee'

import config from '../config'

# First, we will check if the normal crypto library can be used
webCryptoSubtle = window.crypto?.subtle or window.crypto?.webkitSubtle
sha256 = if webCryptoSubtle?.digest
  (arrayBuffer) ->
    dfd = $q.defer()
    webCryptoSubtle
      .digest 'SHA-256', arrayBuffer
      .then (shaBuffer) ->
        dfd.resolve _arrayBufferToHex shaBuffer
    dfd.promise
else if window.msCrypto
  (arrayBuffer) ->
    dfd = $q.defer()
    cryptoOp = window.msCrypto.subtle.digest 'SHA-256', arrayBuffer
    cryptoOp.addEventListener 'abort', -> dfd.reject 'abort'
    cryptoOp.addEventListener 'error', -> dfd.reject 'error'
    cryptoOp.addEventListener 'complete', ->
      dfd.resolve _arrayBufferToHex cryptoOp.result
    dfd.promise
else
  (arrayBuffer) ->
    # TODO: This code fails - so the upload will fail on all browsers without native crypto support or when the browser deactivates its support
    # e.g. Chrome deactivates it when on an insecure connection
    forgeSha256 = forge.md.sha256.create()
    convertedData = new forge.util.ByteStringBuffer arrayBuffer
    forgeSha256.update convertedData.data
    $q.when forgeSha256.digest().toHex()

# Ping every 5 minutes
pingInterval = 5 * 60 * 1000
minParallelParts = 1
minChunkSize = 5 * 1024 * 1024
currentParallelParts = minParallelParts
currentChunkSize = minChunkSize

uploads = []

getRetryInterval = (attempt) ->
  attemptRetryIntervals = [0, 5, 30, 120, 240]
  if attempt >= attemptRetryIntervals.length
    attempt = attemptRetryIntervals.length - 1
  attemptRetryIntervals[attempt]

# Respond to location changes, when there are still pending uploads.
window.onbeforeunload = ->
  # Show a message when there are still pending uploads
  for upload in uploads
    state = upload.getState()
    if state isnt 'initialized' and state isnt 'finished'
      # TODO: Localize the message
      return 'There are still uploads pending. Do you want to abort the current uploads and navigate to the other page?'

  # No upload found (returning null fails in IE)
  return undefined

contactsVariableName = 'ls.contacts'

_loadContactsFromLocalStorage = ->
  contacts = localStorageService.get(contactsVariableName)
  if contacts
    try
      return JSON.parse contacts
    catch

  return []

_saveContactsToLocalStorage = (contacts) ->
  if contacts
    localStorageService.set contactsVariableName, (JSON.stringify contacts)

_deleteUploads = (transfers, removeFromList) ->
  # Obtain all deletes per region
  regionDeletes = {}

  # Cancel the token (this will remove all files on the server)
  for transfer in transfers
    region = transfer.options.region

    # Initialize the region
    regionDeletes[region] ?= {}

    # Obtain the upload token
    if token = transfer._uploadToken
      # Abort the upload
      token.abort()

      # Remove by token (instead of short-url)
      (regionDeletes[region].uploadToken ?= []).push(
        token._internals.serverData.uploadToken
      )
    else if transfer.id
      # If there is no upload-token, then it should be deleted by transfer identifier
      (regionDeletes[region].shortUrl ?= []).push transfer.id

    # Remove this upload from the rootScope
    if removeFromList
      index = uploads.indexOf transfer
      uploads.splice index, 1 if index >= 0

    # Track each delete
    AnalyticsService.dataLayerPush
      event: if transfer.isNewTransfer then 'Upload' else 'ManageUpload'
      uploadStatus: 'Cancel'
      uploadId: if token then token.getId() else transfer.id

  # Obtain all promises
  promises = []
  for region, deleteOptions of regionDeletes
    # Make sure we either have some upload tokens or short-urls
    if deleteOptions.uploadToken or deleteOptions.shortUrl
      # Perform the actual request
      dfd = $q.defer()
      XhrService.delete(
        region
        'upload/delete'
        deleteOptions
        token?.getTransferToken()
      )
      .then (data) -> dfd.resolve data
      .catch (error) -> dfd.reject error

      promises.push dfd.promise

  # Wait until all promises have completed
  $q.all promises

_arrayBufferToHex = (ab) ->
  dv = new DataView ab
  hex = ''
  for i in [0..(dv.byteLength - 1)]
    c = dv.getUint8(i).toString 16
    c = "0#{c}" if c.length < 2
    hex += c
  hex

_sendCurrentPart = (
  token
  currentPart
  partSentCallback
  progressCallback
  dfd
) ->
  # Set the number of attempts to 0
  attempts = 0

  # Increment the number of outstanding bytes
  token._internals.outstandingBytes += currentPart.length
  token._internals.parallelUploads++

  finish = ->
    # Decrement the number of outstanding bytes
    token._internals.outstandingBytes -= currentPart.length
    token._internals.parallelUploads--

    # Remove the promise from the upload array
    token._internals.uploadPartPromises.splice index, 1 if (
      (index = token._internals.uploadPartPromises.indexOf dfd.promise) >= 0
    )

    # Invoke the queued action
    if token._internals.queuedAction
      token._internals.queuedAction()
      delete token._internals.queuedAction

    # Call all slice callbacks
    sliceCallback() for sliceCallback in currentPart.sliceCallbacks
    0

  # Function that handles sending the actual part
  do attemptSending = ->
    # Fail after the maximum allowed attempts
    if token.isAborted()
      # Log error
      $log.warn 'Upload has been aborted...'

      # Finished
      finish()

      # Abort sending
      dfd.reject 'aborted'
    else if attempts >= 5
      # Log error
      $log.error 'Maximum number of attempts reached. Aborting...'

      # Abort the upload
      token.abort()

      # Finished
      finish()

      # Abort sending
      dfd.reject 'max-retries'
    else
      # Try to send the part
      promise = _sendCurrentPartInternal(
        token
        currentPart
        attempts
        progressCallback
      )

      # Resolve when it has succeeded
      promise.then ->
        # Finish uploading
        finish()

        # Block has been sent
        partSentCallback?()

        # Resolve
        dfd.resolve()

      # Retry if it failed or reject it when the number of retries have failed
      promise.catch (error) ->
        # Reject with aborted if sending failed, because it was explicitly aborted
        return dfd.reject 'aborted' if token.isAborted()

        # Increase the number of attempts and retry the operation
        ++attempts

        retryInterval = getRetryInterval attempts
        $log.log "Retrying in #{retryInterval} seconds..."
        setTimeout attemptSending, retryInterval * 1000

  # Return the promise that will be fulfilled when the operation completes
  dfd.promise

_sendCurrentPartInternal = (token, currentPart, attempt, progressCallback) ->
  dfd = $q.defer()

  # Determine the current date (also in XML format)
  startTimestamp = new Date()
  date = startTimestamp.toISOString().replace /[:\-]|\.\d{3}/g, ''

  # Create an alias for the server data
  serverData = token._internals.serverData

  # We now need to create the Authorization header, which is pretty complicated. A
  # complete description can be found on the website of Amazon Web Services:
  # http://docs.aws.amazon.com/AmazonS3/latest/API/sig-v4-header-based-auth.html

  # Create the signature. Each item should be separated with a new-line character.
  # Because a SHA is used, each character counts, so don't add spaces, newlines or
  # mix-up casing. The following entries should be added:
  # 1. HTTP method (PUT)
  # 2. Path within the current host (Canonical URL)
  # 3. Query string
  # 4. All HTTP headers in alphabetic order (each HTTP header on a separate line)
  # 5. Empty line (to indicate the end of the HTTP header)
  # 6. Headers that have been used to sign the message (separated by semicolons)
  # 7. SHA-256 of the actual content (without a newline!!!)
  #
  # Note that the actual signature is never sent to the back-end, but it is only
  # used to create the Authorization header. The signature is recreated by AWS S3
  # based on the information in the request.
  #
  # It's allowed to add more headers in the signature, but the following are
  # required by S3:
  signature =
    'PUT\n' +
    "/#{serverData.bucket}/#{serverData.keyPrefix}-#{
      serverData.multipartFileNumber
    }.zip\n" +
    "partNumber=#{currentPart.partNumber}&uploadId=#{
      serverData.multipartUploadId
    }\n" +
    "host:#{serverData.host}\n" +
    "x-amz-content-sha256:#{currentPart.sha}\n" +
    "x-amz-date:#{date}\n" +
    '\n' +
    'host;x-amz-content-sha256;x-amz-date\n' +
    currentPart.sha

  # Calculate the SHA-256 of the signature.
  signatureSha = sjcl.codec.hex.fromBits sjcl.hash.sha256.hash signature

  # Create the string that will be actually signed.
  stringToSign =
    'AWS4-HMAC-SHA256\n' + "#{date}\n" + "#{serverData.scope}\n" + signatureSha

  # Create the HMAC-SHA256 based on the signing key.
  #
  # IMPORTANT
  # =========
  # This signing key is fetched from the back-end and imposes a security
  # flaw. Using this signing key it is possible to perform arbitrary S3
  # operations. The back-end should make sure that the underlying account
  # of this signing key can only upload data in the designated buckets.
  #
  # If used improperly, clients can be created that can remove complete
  # buckets, list buckets or perform other improper operations!!!
  signingKeyBits = sjcl.codec.hex.toBits serverData.signingKey
  stringToSignBits = sjcl.codec.utf8String.toBits stringToSign
  signatureBits = new sjcl.misc.hmac(signingKeyBits).encrypt stringToSignBits
  signature = sjcl.codec.hex.fromBits signatureBits

  # Create the actual Authorization value
  authorization =
    "AWS4-HMAC-SHA256 Credential=#{serverData.credential}, " +
    'SignedHeaders=host;x-amz-content-sha256;x-amz-date, ' +
    "Signature=#{signature}"

  # Create the XMLHttpRequest that will send the blob to S3
  xhr = new XMLHttpRequest()

  # Keep track of the number of bytes sent
  bytesSent = 0

  # The progress timer is used to ensure that we get progress every minute. If
  # we didn't get any progress within a minute, then the chunk is aborted. This
  # should resolve the 100% stuck uploads that are reported by Safari users.
  progressTimer = progressTimerStarted = null
  startProgressTimer = ->
    # Log that the chunk should be aborted
    #$log.debug "Start progress timer for part ##{currentPart.partNumber} (#{currentPart.blob.size} bytes, attempt #{attempt})"

    progressTimerStarted = Date.now()
    progressTimer = setTimeout(
      ->
        # Log that the chunk should be aborted
        $log.error(
          "Progress timeout while sending part ##{currentPart.partNumber} (#{
            currentPart.blob.size
          } bytes, attempt #{attempt}, #{Date.now() - startTimestamp}ms)"
        )

        # Abort the request
        xhr.abort()

        # Reset the timer
        progressTimer = progressTimerStarted = null
    ,
      3 * 60 * 1000
    )

  updateProgress = (newBytesSent, isFinished) ->
    # Cancel the progress timer
    if progressTimer
      # Don't restart the timer within 5 seconds
      # (we would have too many timers)
      if isFinished or Date.now() - progressTimerStarted > 5000
        # Cancel the timer
        clearTimeout progressTimer
        progressTimer = progressTimerStarted = null

        # Restart the progress timer
        startProgressTimer() if !isFinished

    # Determine the delta in the number of bytes sent
    delta = newBytesSent - bytesSent
    bytesSent = newBytesSent

    # Update the number of bytes sent
    progressCallback? delta if delta

  # Add the request to the token
  token._internals.xhrs.push xhr

  removeRequest = ->
    # Remove the request from the list
    index = token._internals.xhrs.indexOf xhr
    token._internals.xhrs.splice index, 1 if index >= 0

  rejectWithError = (message, rejectCode) ->
    # Log warning
    $log.warn(
      "Error sending part ##{currentPart.partNumber} (#{
        currentPart.blob.size
      } bytes, attempt #{attempt}, #{Date.now() -
        startTimestamp}ms, #{message})"
    )

    # Reset upload parameters
    currentParallelParts = minParallelParts
    currentChunkSize = minChunkSize

    updateProgress 0, true
    removeRequest()
    dfd.reject rejectCode

  # Register for the XHR events
  xhr.addEventListener 'load', ->
    # The HTTP command was completed, but only codes 200-300 mean success
    httpStatus = xhr.status
    return rejectWithError "error: #{httpStatus}", "http-#{httpStatus}" if (
      httpStatus > 200 or httpStatus >= 300
    )

    # Remove the request
    removeRequest()

    # Determine duration
    duration = Date.now() - startTimestamp

    # Determine speed
    uploadSpeed = Math.round currentPart.blob.size / duration
    if uploadSpeed > 1000
      currentParallelParts = 5
      currentChunkSize = 25 * 1024 * 1024
    else if uploadSpeed > 500
      currentParallelParts = 3
      currentChunkSize = 20 * 1024 * 1024
    else if uploadSpeed > 100
      currentParallelParts = 2
      currentChunkSize = 10 * 1024 * 1024
    else
      currentParallelParts = 1
      currentChunkSize = minChunkSize

    # Log success
    $log.log(
      "Finished sending part ##{currentPart.partNumber} (#{
        currentPart.blob.size
      } bytes, attempt #{attempt}, #{duration}ms, #{uploadSpeed}kb/sec)"
    )

    # Obtain the ETag from the response
    etag = xhr.getResponseHeader('ETag')[1..-2]

    # Add the current part in the administration
    token._internals.parts.push
      partNumber: currentPart.partNumber
      etag: etag

    # Update progress for the last time with the final blob size
    updateProgress currentPart.blob.size, true

    # Resolve sending this part
    dfd.resolve()

  # Respond to upload errors
  xhr.addEventListener 'error', -> rejectWithError 'error on network', 'error'
  xhr.addEventListener 'abort', -> rejectWithError 'request aborted', 'abort'
  xhr.addEventListener 'timeout', ->
    rejectWithError 'request timed out', 'timeout'

  # Respond to upload progress callback
  xhr.upload.addEventListener 'progress', (evt) ->
    updateProgress evt.loaded, false

  # DEBUG START
  if attempt > 0
    xhr.onreadystatechange = ->
      $log.info(
        "Ready-state changed for part ##{currentPart.partNumber} (#{
          currentPart.blob.size
        } bytes, attempt #{attempt}, #{Date.now() -
          startTimestamp}ms, readystate: #{xhr.readyState}, status: #{
          xhr.status
        })"
      )
  # DEBUG END

  # Start sending the data
  xhr.open(
    'PUT'
    "#{config.uploadservice.protocol}://#{serverData.host}/#{serverData.bucket}/#{serverData.keyPrefix}-#{
      serverData.multipartFileNumber
    }.zip?partNumber=#{currentPart.partNumber}&uploadId=#{
      serverData.multipartUploadId
    }"
  )
  xhr.setRequestHeader 'Authorization', authorization
  xhr.setRequestHeader 'X-Amz-Content-Sha256', currentPart.sha
  xhr.setRequestHeader 'X-Amz-Date', date
  xhr.send currentPart.blob

  # Restart the progress timer
  startProgressTimer()

  # Log that we have started sending
  #$log.log "Started sending part ##{currentPart.partNumber} (#{currentPart.blob.size} bytes, attempt #{attempt})"
  dfd.promise

_sendSidecar = (
  token
  formData
  totalBytes
  fileInfo
  attempt
  progressCallback
) ->
  dfd = $q.defer()

  # Keep track of the number of bytes sent
  bytesSent = 0
  updateProgress = (newBytesSent) ->
    # Determine the delta in the number of bytes sent
    delta = newBytesSent - bytesSent
    bytesSent = newBytesSent

    # Update the number of bytes sent
    progressCallback? delta if delta

  # Create the XMLHttpRequest that will send the blob to S3
  xhr = new XMLHttpRequest()
  startTimestamp = new Date()

  # Add the request to the token
  token._internals.xhrs.push xhr

  removeRequest = ->
    # Remove the request from the list
    index = token._internals.xhrs.indexOf xhr
    token._internals.xhrs.splice index, 1 if index >= 0

  rejectWithError = (message, rejectCode) ->
    # Log warning
    $log.warn(
      "Error sending sidecar #{
        fileInfo.name
      } (#{totalBytes} bytes, attempt #{attempt}, #{Date.now() -
        startTimestamp}ms, #{message})"
    )

    updateProgress 0
    removeRequest()
    dfd.reject rejectCode

  # Register for the XHR events
  xhr.addEventListener 'load', ->
    # The HTTP command was completed, but only codes 200-300 mean success
    httpStatus = xhr.status
    return rejectWithError "error: #{httpStatus}", "http-#{httpStatus}" if (
      httpStatus < 200 or httpStatus >= 300
    )

    # Remove the request
    removeRequest()

    # Determine duration of request
    duration = Date.now() - startTimestamp

    # Log success
    $log.log(
      "Finished sending sidecar #{
        fileInfo.name
      } (#{totalBytes} bytes, attempt #{attempt}, #{duration}ms, #{Math.round(
        totalBytes / duration
      )}kb/sec)"
    )

    updateProgress totalBytes
    dfd.resolve()

  # Respond to error callbacks
  xhr.addEventListener 'error', -> rejectWithError 'error on network', 'error'
  xhr.addEventListener 'abort', -> rejectWithError 'request aborted', 'abort'
  xhr.addEventListener 'timeout', ->
    rejectWithError 'request timed out', 'timeout'

  # Respond to upload progress callbacks
  xhr.upload.addEventListener 'progress', (evt) -> updateProgress evt.loaded

  # Obtain the server data
  serverData = token._internals.serverData

  # Post to the specified bucket
  xhr.open 'POST', "#{config.uploadservice.protocol}://#{serverData.host}/#{serverData.bucket}"
  xhr.send formData

  # Log that we have started sending
  #$log.log "Started sending sidecar #{fileInfo.name} (#{totalBytes} bytes, attempt #{attempt})"
  dfd.promise

_completePart = (token, part, partSentCallback, progressCallback) ->
  # Copy the current part fields (shallow copy)
  thisPart = Object.assign {}, part

  # Create the BLOB
  thisPart.blob = new Blob thisPart.buffers, type: 'application/octet-stream'

  # Keep track of SHA calculation times
  startTimestamp = new Date()

  # Create deferred object for the finish-upload promise
  dfdFinishedUploading = $q.defer()
  token._internals.uploadPartPromises.push dfdFinishedUploading.promise

  # Create deferred object for the ready-for-next-part promise
  dfdReadyForNextPart = $q.defer()

  # Start reading the blob
  fr = new FileReader()
  fr.onload = ->
    # Check if the size matches
    $log.error(
      "FileReader read #{fr.result.byteLength} instead of #{
        thisPart.blob.size
      } bytes for part #{part.partNumber}"
    ) if fr.result.byteLength isnt thisPart.blob.size

    sha256(fr.result).then (sha) ->
      # Log SHA calculation time
      #$log.log "SHA calculation part ##{thisPart.partNumber} (#{fr.result.byteLength} bytes, #{new Date() - startTimestamp}ms)"

      # Save SHA as a HEX value
      thisPart.sha = sha

      action = ->
        if !token.isAborted()
          _sendCurrentPart(
            token
            thisPart
            partSentCallback
            progressCallback
            dfdFinishedUploading
          )
        dfdReadyForNextPart.resolve()

      # Queue this item
      if token._internals.uploadPartPromises.length <= currentParallelParts
        action()
      else
        token._internals.queuedAction = action

  # Read the data and return
  fr.readAsArrayBuffer thisPart.blob

  # Ready for the next part
  newPart = token._internals.currentPart
  newPart.partNumber++
  newPart.length = 0
  newPart.buffers = []
  newPart.sliceCallbacks = []
  dfdReadyForNextPart.promise

_regions = null
_activePromise = null

export default
  getChunkSize: ->
    currentChunkSize

  getRegions: (forceRefresh) ->
    # If some-one else is already obtaining the regions, then we can use their DFD
    return _activePromise if _activePromise

    # We need to check it ourselves
    dfd = $q.defer()
    promise = dfd.promise
    if forceRefresh or !_regions
      # We are actively obtaining the regions
      _activePromise = promise

      waitForFastestRegion = (resolve) ->
        if fastestRegion = ApiService.getFastestRegion()
          return resolve fastestRegion
        else
          setTimeout (-> waitForFastestRegion resolve), 100

      new $q((resolve) -> waitForFastestRegion resolve).then ->
        # Get the regions
        XhrService.get 'cloudfront', "getRegions?defaultRegion=#{ApiService.getFastestRegion()}"
        .then (data) ->
          _regions = data

          # Add localhost and China if the developer mode is enabled. Note that
          # the regions are cached, so developer mode should be enabled when
          # this call is made
          if !isProduction
            for region, index in _regions.regions
              switch region.mnemonic
                when 'localhost'
                  # Make sure we can use the localhost and if it's the default
                  _regions.defaultRegion = index if region.active

                when 'eu-west-1'
                  # Make sure we can upload in Ireland (for testing)
                  region.active = true
                  _regions.defaultRegion = index if (
                    _regions.regions[_regions.defaultRegion].mnemonic isnt
                    'localhost'
                  )

          _activePromise = null
          dfd.resolve _regions
        .catch (error) ->
          _regions = null
          _activePromise = null
          dfd.reject error
    else
      dfd.resolve _regions
    promise

  getContacts: ->
    # Obtain all the available user contacts
    dfd = $q.defer()

    # If the user is logged on (non-restricted),
    # then we try to obtain the contacts from the profile
    profile = LoginService.getProfile()
    if profile and !profile.restriction
      # Obtain the contact from the profile (fallback to local storage)
      XhrService.get null, 'user/contacts'
        .then (data) -> dfd.resolve data
        .catch -> dfd.resolve _loadContactsFromLocalStorage()
    else
      dfd.resolve _loadContactsFromLocalStorage()
    dfd.promise

  getUploads: ->
    uploads

  registerUpload: (transfer) ->
    # Register transfer in list of active uploads
    uploads.push transfer

  checkUpload: (region, email) ->
    dfd = $q.defer()
    XhrService.get region, 'upload/check', from: email
      .then (data) -> dfd.resolve data
      .catch (error) -> dfd.reject error

    # Return the promise
    dfd.promise

  startUpload: (transfer, region, confirmOptions) ->
    # Create a new token
    token = new UploadToken transfer, region, confirmOptions

    dataToSend =
      sessionId: debugLoggingId
      language: LanguageService.getLanguage()
      files: {}

    # Add the referrer (if known)
    dataToSend.referer = document.referrer if document.referrer

    # Keep track of a list that should be confirmed at the server
    saveItems = []

    # Add all folders
    for key, folderInfo of transfer.folders
      if !folderInfo.backEndStatus
        folderData = {}

        # Set basic file information
        folderData.name = folderInfo.name
        folderData.folderKey = folderInfo.folder.getKey() if (
          folderInfo.folder?.id?
        )
        folderData.isFolder = true

        saveItems.push folderInfo
        dataToSend.files[folderInfo.getKey()] = folderData

    # Add all files that are not already on the back-end
    for key, fileInfo of transfer.files
      # Only process files that are saved to the back-end
      if !fileInfo.backEndStatus
        fileData = {}

        # Set basic file information
        fileData.name = fileInfo.name
        fileData.folderKey = fileInfo.folder.getKey() if fileInfo.folder?.id?
        fileData.size = fileInfo.size
        fileData.type = fileInfo.type if fileInfo.type
        fileData.lastModifiedDate = fileInfo.lastModifiedDate if (
          fileInfo.lastModifiedDate
        )

        # Add the sidecar information
        if fileInfo.linkedFiles?.length
          fileData.sidecarKeys = []
          for linkedFile in fileInfo.linkedFiles
            fileData.sidecarKeys.push linkedFile.getKey()

        # Set compression level (if enabled)
        fileData.compressionLevel = fileInfo.compressionLevel if (
          fileInfo.compressionLevel
        )

        saveItems.push fileInfo
        dataToSend.files[fileInfo.getKey()] = fileData

    # Use different options for start for new and existing uploads
    if transfer.isNewTransfer
      dataToSend.from = confirmOptions.fromEmail
      dataToSend.shareMethod = confirmOptions.shareMethod
      dataToSend.message = confirmOptions.message
      dataToSend.aesEncryption = if confirmOptions.isEncrypted then 3 else 0
      dataToSend.download_confirmation = true
      dataToSend.allow_instant_download = true

      # Check if the confirm options are valid
      switch confirmOptions.shareMethod
        when 'email'
          # Check if at least one recipient is set
          throw new Error 'No email settings specified' if !confirmOptions.email
          throw new Error 'A recipient should be specified' if (
            !confirmOptions.email.recipients
          )

          # Add the to email addresses
          dataToSend.to = confirmOptions.email.recipients
          dataToSend.delete_after_download = confirmOptions.email
            .deleteAfterDownload

          # Load the contacts from local storage
          contacts = _loadContactsFromLocalStorage()

          # Add/update the contacts
          for recipient in confirmOptions.email.recipients
            index = contacts.indexOf recipient
            contacts.splice index, 1 if index >= 0
            contacts.unshift recipient

          # If there were any updates, then save the contacts again
          _saveContactsToLocalStorage contacts

        when 'twitter', 'facebook', 'link'
        # NOP
          ;

        else
          # Invalid sharing method
          throw new Error(
            "Invalid shareMethod '#{confirmOptions.shareMethod}' specified"
          )

      # Set the confirmation options
      token.confirmOptions = confirmOptions
    else
      # Set the existing short-url
      dataToSend.shortUrl = transfer.id

    doStartUpload = (recaptchaToken) ->
      dfdInner = $q.defer()
      dataToSend.recaptchaToken = recaptchaToken if recaptchaToken

      XhrService.post region, 'upload/start', dataToSend
      .then (data) ->
        # All items are saved in the back-end
        item.backEndStatus = 'saved' for item in saveItems

        # Save the S3 data
        token._internals.serverData = data

        # Obtain the multi-part data
        token._internals.parts = []
        token._internals.uploadPartPromises = []
        token._internals.currentPart =
          totalLength: 0
          partNumber: 1
          length: 0
          buffers: []
          sliceCallbacks: []

        # Track events
        AnalyticsService.dataLayerPush
          event: if transfer.isNewTransfer then 'Upload' else 'ManageUpload'
          uploadStatus: 'Start'
          storageRegion: transfer.options.region
          encryption: if confirmOptions.isEncrypted then 'yes' else 'no'
          share: confirmOptions.shareMethod
          files: transfer.getTotalFileCount()
          filesize: transfer.getTotalUploadSize()
          uploadId: token.getId()
        AnalyticsService.adConversionTrack '957553998', 'c6d5CNv6tFYQzrrMyAM' if transfer.isNewTransfer

        # Determine the ping functionality
        token.pingCounter = 0
        pingMethod = ->
          pingCounter = token.pingCounter++
          XhrService.put transfer.options.region, 'upload/ping',
            uploadToken: data.uploadToken
            pingCounter: pingCounter

          .then -> $log.log "Pinged transfer ##{pingCounter}"
          .catch (error) -> $log.warn "Pinged transfer ##{pingCounter} (error: #{error})"

        # Start the ping method
        token.ping = setInterval pingMethod, pingInterval

        # Resolve using our token
        dfdInner.resolve token
      .catch (error) ->
        dfdInner.reject error

      dfdInner.promise

    dfd = $q.defer()

    grecaptcha.ready () ->
      grecaptcha.execute config.grecaptchaWidgetId, {action: 'startUpload'}
      .then (recaptchaToken) ->
        doStartUpload recaptchaToken
        .then (token) -> dfd.resolve token
        .catch (e) -> dfd.reject e
      .catch (e) ->
        doStartUpload null
        .then (token) -> dfd.resolve token
        .catch (e) -> dfd.reject e

    # Return the promise
    dfd.promise

  finishUpload: (token, avgTransferRate) ->
    # The finish upload cannot be called, when the token has been aborted
    if token.isAborted()
      $log.warn(
        "Cannot confirm upload '#{token.getId()}', because the upload has been aborted."
      )
      return

    # Obtain the transfer
    transfer = token._internals.transfer
    serverData = token._internals.serverData

    # Sort the part-numbers
    token._internals.parts.sort (a, b) -> a.partNumber - b.partNumber

    # Setup the file list
    dataToSend =
      language: LanguageService.getLanguage()
      uploadToken: serverData.uploadToken
      files: {}
      multipart:
        uploadId: serverData.multipartUploadId
        fileNumber: serverData.multipartFileNumber
        parts: token._internals.parts

    # Setup the file list
    finishItems = []
    for key, fileInfo of transfer.files
      # Sidecars can be skipped
      if fileInfo.backEndStatus is 'saved'
        fileData = {}

        # Set basic file information
        fileData.offset = fileInfo.zipDataOffset
        fileData.compressedSize = fileInfo.compressedSize if (
          fileInfo.compressedSize
        )
        fileData.crc = fileInfo.crc if fileInfo.crc

        finishItems.push fileInfo
        dataToSend.files[fileInfo.getKey()] = fileData

    # Cancel the transfer ping
    if token.ping
      clearInterval token.ping
      delete token.ping

    # Perform the actual request
    dfd = $q.defer()
    XhrService.post transfer.options.region, 'upload/finish', dataToSend
      .then ->
        # Track event
        AnalyticsService.dataLayerPush
          event: if transfer.isNewTransfer then 'Upload' else 'ManageUpload'
          uploadStatus: 'Finish'
          uploadRate: avgTransferRate
          uploadId: token.getId()

        # All items are saved in the back-end
        (item.backEndStatus = 'finished') for item in finishItems

        # Remove the status of all folders
        delete folder.status for folder in transfer.folders

        # Dump all compression statistics
        for fileType, data of token._internals.compression
          AnalyticsService.dataLayerPush
            event: 'Compression'
            fileType: fileType
            ratio: Math.round (data.cSize / data.uSize) * 100

        # Resolve with the token
        dfd.resolve token
      .catch (error) ->
        # Reject with error
        dfd.reject error

    dfd.promise

  getZipOffset: (token) ->
    token._internals.currentPart.totalLength

  uploadRawPart: (
    token
    buffer
    partSentCallback
    progressCallback
    sliceCallback
  ) ->
    # UploadPart adds the specified block to a multi-part upload
    # part. It returns a promise that will be fulfilled when the
    # next part can be added.

    # Obtain size of buffer
    bufferSize = buffer.size || buffer.byteLength

    # Keep track of the current part
    currentPart = token._internals.currentPart

    # Determine if we have to add the new part to the current list
    # of blocks to send. We will send out the current block, if
    #  1. We have more than 5MB in the current part, AND
    #  2. The total of the current and new part is more than 10MB
    if (
      currentPart.length > minChunkSize and
      currentPart.length + bufferSize > currentChunkSize
    )
      promise = _completePart(
        token
        currentPart
        partSentCallback
        progressCallback
      )
    else
      promise = $q.when true

    # Add the current part
    currentPart.buffers.push buffer
    currentPart.length += bufferSize
    currentPart.totalLength += bufferSize
    currentPart.sliceCallbacks.push sliceCallback if sliceCallback

    # Return the promise
    promise

  uploadPart: (
    token
    fileInfo
    buffer
    chunk
    partSentCallback
    progressCallback
    sliceCallback
  ) ->
    # Upload the part
    promise = @uploadRawPart(
      token
      buffer
      partSentCallback
      progressCallback
      sliceCallback
    )

    # Send compression ratio when sending the last chunk
    if fileInfo.compressionLevel and chunk is fileInfo.totalChunks - 1
      # Determine the file-type
      fileType = fileInfo.type.toLowerCase()

      # Obtain compression statistics for this file-type
      compressionStatistics = token._internals.compression[fileType] ?=
        cSize: 0
        uSize: 0

      # Update compression statistics
      compressionStatistics.cSize += fileInfo.compressedSize
      compressionStatistics.uSize += fileInfo.size

    # Complete the promise when the current chunk has been scheduled for sending.
    promise

  finishUploadParts: (token, partSentCallback, progressCallback) ->
    # Keep track of the current part
    currentPart = token._internals.currentPart

    # Send the current part (if there is still some data)
    if currentPart.length > 0
      _completePart token, currentPart, partSentCallback, progressCallback

    # Wait until all the parts have been sent
    $q.all token._internals.uploadPartPromises

  uploadSidecar: (token, fileInfo, blob, progressCallback, maxAttempts = 5) ->
    # Obtain the S3 data
    serverData = token._internals.serverData

    # Setup the form data
    formData = new FormData()
    formData.append 'acl', serverData.acl
    formData.append 'success_action_status', serverData.successActionStatus
    formData.append 'policy', serverData.policy
    formData.append 'Content-Type', fileInfo.type || ''
    formData.append 'x-amz-algorithm', 'AWS4-HMAC-SHA256'
    formData.append 'x-amz-credential', serverData.credential
    formData.append 'x-amz-date', serverData.date
    formData.append 'x-amz-signature', serverData.signature

    # Add server-side encryption (if requested)
    if serverData.encryptionType
      formData.append 'x-amz-server-side-encryption', serverData.encryptionType

    # Add key
    formData.append 'key', "#{serverData.keyPrefix}-#{fileInfo.getKey()}"

    # Add the blob itself
    formData.append 'file', blob, fileInfo.name

    # Create the deferred object that will be set when the part has been sent completely
    dfd = $q.defer()

    # Increment the number of outstanding bytes
    token._internals.outstandingBytes += blob.size
    token._internals.parallelUploads++

    # Function that handles sending the actual part
    attempts = 0
    do attemptSending = ->
      # Fail after the maximum allowed attempts
      if attempts >= maxAttempts
        # Log error
        $log.error(
          "#{
            fileInfo.name
          }: Maximum number of attempts reached. Bailing out (sidecar won't be available)..."
        )

        # Decrement the number of outstanding bytes
        token._internals.outstandingBytes -= blob.size
        token._internals.parallelUploads--

        # Abort sending
        dfd.reject 'max-retries'
      else
        # Try to send the part
        promise = _sendSidecar(
          token
          formData
          blob.size
          fileInfo
          attempts
          progressCallback
        )

        # Resolve when it has succeeded
        promise.then (result) ->
          # Decrement the number of outstanding bytes
          token._internals.outstandingBytes -= blob.size
          token._internals.parallelUploads--

          # Resolve
          dfd.resolve result

        # Retry if it failed or reject it when the number of retries have failed
        promise.catch (error) ->
          # Reject with aborted if sending failed, because it was explicitly aborted
          return dfd.reject 'aborted' if token.isAborted()

          # Increase the number of attempts and retry the operation
          ++attempts

          retryInterval = getRetryInterval attempts
          $log.log "Retrying in #{retryInterval} seconds..."
          setTimeout attemptSending, retryInterval * 1000

    # Return the promise that completes when this chunk has been sent
    dfd.promise

  deleteUploads: (transfers, removeFromList) ->
    _deleteUploads transfers, removeFromList

  removeItems: (token, transfer, folders, files) ->
    dfd = $q.defer()

    # Minify the amount of items that we send to the back-end. If the transfer hasn't been confirmed,
    # then we don't minify, because the backend hasn't the information to know what is in which folder.
    if transfer.confirmedUpload
      folders = folders.slice()
      files = files.slice()
      transfer.minify folders, files

    # Get a sorted list of keys
    keys = folders
      .sort (f) -> f.id
      .concat files.sort (f) -> f.id
      .map (f) -> f.getKey()

    # Delete the items
    data =
      shortUrl: transfer.id
      keys: keys
    XhrService.delete transfer.options.region, 'upload/deleteFiles', data
      .then ->
        dfd.resolve()
      .catch (error) ->
        dfd.reject error

    dfd.promise
